import type Handsontable from 'handsontable';
import type { CellType } from 'handsontable/cellTypes';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { ReactNode } from 'react';
import type { RangeType } from '@/support/hot.types';
import type {
  CustomColumnSettings,
  CustomValidation,
  SpreadsheetSettings,
  SpreadSheetError,
} from './SpreadSheet.model';

export type SpreadSheetErrorMessage = (value: ReactNode, rowData?: unknown) => string;

export const spreadSheetValidationTooltip = 'There are invalid cells';

export const isHotRowEmptyForCopy = (hot: Handsontable.Core, row: number): boolean => {
  const colCount = hot.countCols();

  for (let col = 0; col < colCount; col++) {
    const cellValue = hot.getDataAtCell(row, col);
    const { type } = hot.getCellMeta(row, col);
    if (type === 'checkbox') {
      if (cellValue === true) {
        return false;
      }
      continue;
    }
    if (Array.isArray(cellValue)) {
      if (_isNotEmpty(cellValue)) {
        return false;
      }
      continue;
    }
    if (_notNil(cellValue) && cellValue !== '') {
      return false;
    }
  }
  return true;
};

export const isHotSpareRowForCopy = (hot: Handsontable.Core, row: number, minSpareRows: number): boolean => {
  if (minSpareRows <= 0) {
    return false;
  }
  if (row < hot.countRows() - minSpareRows) {
    return false;
  }
  return isHotRowEmptyForCopy(hot, row);
};

export const excludeSpareRowsFromCopyableRanges = (
  hot: Handsontable.Core,
  copyableRanges: Array<RangeType>,
  minSpareRows: number
): Array<RangeType> => {
  if (minSpareRows <= 0 || copyableRanges.length === 0) {
    return copyableRanges;
  }

  return copyableRanges.reduce<Array<RangeType>>((acc, range) => {
    let endRow = range.endRow;

    while (endRow >= range.startRow && isHotSpareRowForCopy(hot, endRow, minSpareRows)) {
      endRow--;
    }

    if (endRow >= range.startRow) {
      acc.push(endRow === range.endRow ? range : { ...range, endRow });
    }

    return acc;
  }, []);
};

export const createExcludeSpareRowsFromCopyHook = (minSpareRows: number) =>
  function excludeSpareRowsFromCopy(this: Handsontable.Core, copyableRanges: Array<RangeType>): Array<RangeType> {
    return excludeSpareRowsFromCopyableRanges(this, copyableRanges, minSpareRows);
  };

export const formatValidationErrors = (errors: Record<string, SpreadSheetError>): Array<string> =>
  Object.keys(errors).map((column: string) => createValidationError(errors[column], ''));

export const createValidationError = (
  error: SpreadSheetError,
  type?: string,
  customMessage?: SpreadSheetErrorMessage,
  rowData?: unknown
): string => {
  if ((_isNil(error.value) || error.value === '') && _isNil(customMessage)) {
    return 'Required Field';
  } else {
    return customMessage?.(error.value, rowData) ?? errorCellTypes[type as CellType]?.(error.value) ?? `Is not valid`;
  }
};

export const buildCustomValidators = (columns: Array<CustomColumnSettings>): Record<string, CustomValidation> =>
  columns.reduce<Record<string, CustomValidation>>((acc, { data, validationErrorMessage, linkedValidationColumns }) => {
    if (_notNil(data)) {
      return { ...acc, [data.toString()]: { validationErrorMessage, linkedValidationColumns } };
    } else {
      return acc;
    }
  }, {});

/**
 * Strict empty check for hide-empty-columns: null/undefined/'' only.
 * Plain objects use `display_value`; whitespace is not empty.
 */
export const isCellEmptyForHide = (cell: unknown): boolean => {
  if (_notNil(cell) && typeof cell === 'object' && !Array.isArray(cell)) {
    const displayValue = (cell as { display_value?: unknown }).display_value;
    return displayValue === null || displayValue === undefined || displayValue === '';
  }
  return cell === null || cell === undefined || cell === '';
};

export const isColumnEmptyAcrossRows = (data: Array<Record<string, unknown>>, columnKey: string): boolean =>
  data.every((row) => isCellEmptyForHide(row[columnKey]));

const isRecordRowData = (
  data: Array<Record<string, unknown>> | Array<Array<unknown>>
): data is Array<Record<string, unknown>> => {
  if (_isEmpty(data)) {
    return false;
  }
  const firstRow = data[0];
  return _notNil(firstRow) && typeof firstRow === 'object' && !Array.isArray(firstRow);
};

/**
 * Immutable filter of empty read-only columns listed in `hideEmptyColumns`.
 * Returns the same settings reference when there is nothing to hide (including empty data).
 */
export const applyHideEmptyColumns = (
  settings: SpreadsheetSettings,
  data: Array<Record<string, unknown>> | Array<Array<unknown>>,
  hideEmptyColumns?: Array<string>
): SpreadsheetSettings => {
  if (_isEmpty(hideEmptyColumns) || _isEmpty(data) || !isRecordRowData(data)) {
    return settings;
  }

  const columns = settings.columns;
  if (_isEmpty(columns) || !Array.isArray(columns)) {
    return settings;
  }

  const hideKeys = new Set(hideEmptyColumns);
  const keptIndices = columns.reduce<Array<number>>((acc, column, index) => {
    const dataKey = column.data;
    const isHideCandidate = column.readOnly === true && typeof dataKey === 'string' && hideKeys.has(dataKey);

    if (isHideCandidate && isColumnEmptyAcrossRows(data, dataKey)) {
      return acc;
    }

    acc.push(index);
    return acc;
  }, []);

  if (keptIndices.length === columns.length) {
    return settings;
  }

  const nextColumns = keptIndices.map((index) => columns[index]);
  const colHeaders = settings.colHeaders;
  const nextColHeaders = Array.isArray(colHeaders) ? keptIndices.map((index) => colHeaders[index]) : colHeaders;

  return {
    ...settings,
    columns: nextColumns,
    colHeaders: nextColHeaders,
  };
};

type CustomCellTypes = 'numeric-expo' | 'multi-select' | 'date-wrapper' | 'color' | 'datetime';

const errorCellTypes: Record<CellType | CustomCellTypes, SpreadSheetErrorMessage> = {
  dropdown: () => `Is not a valid option`,
  select: () => `Is not a valid option`,
  date: () => `Must be a valid date`,
  numeric: (value) => `${value ?? 'Value'} must be a number`,
  time: () => `Must be a valid time`,
  autocomplete: () => `Is not valid`,
  handsontable: () => `Is not valid`,
  password: () => `Is not valid`,
  text: () => `Must have a max length of 255 characters`,
  checkbox: () => `Is not valid`,
  'numeric-expo': () => `Value must be a number`,
  'multi-select': () => `Is not a valid option`,
  'date-wrapper': () => `Must be a valid YYYY-MM-DD date`,
  color: () => `Must be a valid hex color`,
  datetime: () => `Must be a valid date and time`,
};
